import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

/**
 * TEMU 库存新增/编辑弹窗。
 * - full: JIT 增减（virtualStockDiff）
 * - semi: 覆盖目标库存，或新增仓站路由
 */
export default function TemuStockMutationModal({
  open,
  mode = 'edit', // edit | add
  track = 'full',
  row = null,
  confirming = false,
  onConfirm,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const isSemi = track === 'semi' || track === 'local';
  const isAdd = mode === 'add';

  const [diff, setDiff] = useState('');
  const [target, setTarget] = useState('');
  const [productId, setProductId] = useState('');
  const [productSkuId, setProductSkuId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [siteIds, setSiteIds] = useState('');

  useEffect(() => {
    if (!open) return;
    setDiff('');
    setTarget(row?.skuStockQuantity != null ? String(row.skuStockQuantity) : '');
    setProductId('');
    setProductSkuId(row?.productSkuId != null ? String(row.productSkuId) : '');
    setWarehouseId(row?.warehouseId ? String(row.warehouseId) : '');
    setSiteIds(row?.siteCode ? String(row.siteCode) : '');
  }, [open, row]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, onClose]);

  const title = useMemo(() => {
    if (isAdd) return t('stock.addTitle');
    return isSemi ? t('stock.editWh') : t('stock.editJit');
  }, [isAdd, isSemi, t]);

  if (!open) return null;

  const submit = () => {
    if (isAdd) {
      const pid = Number(productId);
      const wid = String(warehouseId || '').trim();
      const qty = Number(target);
      const sku = Number(productSkuId);
      const sites = String(siteIds || '')
        .split(/[,，\s]+/)
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (!Number.isFinite(pid) || pid <= 0) {
        onConfirm?.({ error: t('stock.needProductId') });
        return;
      }
      if (!wid) {
        onConfirm?.({ error: t('stock.needWarehouseId') });
        return;
      }
      if (!Number.isFinite(qty) || qty < 0) {
        onConfirm?.({ error: t('stock.needTarget') });
        return;
      }
      if (!sites.length) {
        onConfirm?.({ error: t('stock.needSite') });
        return;
      }
      const stockItem = {
        warehouseId: wid,
        targetStockAvailable: qty,
      };
      if (Number.isFinite(sku) && sku > 0) {
        stockItem.productSkuId = sku;
      }
      onConfirm?.({
        payload: {
          productId: pid,
          addwarehouseSkuStockList: [stockItem],
          addWarehouseSiteList: [{ warehouseId: wid, siteIdList: sites }],
        },
      });
      return;
    }

    if (isSemi) {
      const sku = Number(row?.productSkuId || productSkuId);
      const wid = String(row?.warehouseId || warehouseId || '').trim();
      const qty = Number(target);
      if (!Number.isFinite(sku) || sku <= 0 || !wid) {
        onConfirm?.({ error: t('stock.needSkuWh') });
        return;
      }
      if (!Number.isFinite(qty) || qty < 0) {
        onConfirm?.({ error: t('stock.needTarget') });
        return;
      }
      onConfirm?.({
        payload: {
          quantityChangeMode: 2,
          skuStockChangeList: [{
            productSkuId: sku,
            warehouseId: wid,
            targetStockAvailable: qty,
          }],
        },
      });
      return;
    }

    const skc = Number(row?.productSkcId);
    const sku = Number(row?.productSkuId);
    const d = Number(diff);
    if (!Number.isFinite(skc) || skc <= 0 || !Number.isFinite(sku) || sku <= 0) {
      onConfirm?.({ error: t('stock.needSkcSku') });
      return;
    }
    if (!Number.isFinite(d) || d === 0) {
      onConfirm?.({ error: t('stock.needDiff') });
      return;
    }
    onConfirm?.({
      payload: {
        skuVirtualStockChangeList: [{
          productSkcId: skc,
          productSkuId: sku,
          virtualStockDiff: d,
        }],
      },
    });
  };

  return createPortal(
    <div className="tpo-batch-modal" role="presentation">
      <div className="tpo-batch-modal__backdrop" onClick={() => !confirming && onClose?.()} />
      <div className="tpo-batch-modal__panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="tpo-batch-modal__head">
          <h3 className="tpo-batch-modal__title">{title}</h3>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" disabled={confirming} onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body">
          {isAdd ? (
            <>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.productId')}</span>
                <input className="tpo-input" value={productId} onChange={(e) => setProductId(e.target.value)} disabled={confirming} />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.skuOptional')}</span>
                <input className="tpo-input" value={productSkuId} onChange={(e) => setProductSkuId(e.target.value)} disabled={confirming} />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.warehouseId')}</span>
                <input className="tpo-input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} disabled={confirming} />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.siteIds')}</span>
                <input className="tpo-input" value={siteIds} onChange={(e) => setSiteIds(e.target.value)} disabled={confirming} placeholder={t('stock.sitePh')} />
              </label>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.targetQty')}</span>
                <input className="tpo-input" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} disabled={confirming} />
              </label>
            </>
          ) : isSemi ? (
            <>
              <p className="tpo-muted">{t('stock.skuLine', { sku: row?.productSkuId ?? '—', warehouse: row?.warehouseId || '—' })}</p>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.targetCover')}</span>
                <input className="tpo-input" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} disabled={confirming} />
              </label>
            </>
          ) : (
            <>
              <p className="tpo-muted">{t('stock.jitLine', { skc: row?.productSkcId ?? '—', sku: row?.productSkuId ?? '—', qty: row?.skuStockQuantity ?? '—' })}</p>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('stock.diffLabel')}</span>
                <input className="tpo-input" type="number" value={diff} onChange={(e) => setDiff(e.target.value)} disabled={confirming} placeholder={t('stock.diffPh')} />
              </label>
            </>
          )}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" disabled={confirming} onClick={onClose}>
            {t('shop.cancel')}
          </button>
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" disabled={confirming} onClick={submit}>
            {confirming ? t('returnList.submitting') : t('productTemplate.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
